import { requestServices } from "@/utils/request";
import { getUserAccessToken } from "@/utils/helpers/auth";
import { withNotSyncedRetry } from "@/utils/helpers/not-synced-retry";
import {
  ICheckInConfig,
  IOrderTokenHistory,
  IRedeemByBarCodePayload,
  IRedeemByBarCodeResult,
  IRedeemResult,
  IRedeemTokensPayload,
  ITicketScanHistory,
} from "@/types/check-in";

// requestServices sends no authentication. The engine reads the Cognito
// access token and compares its groups with the groupId in the request.
function bearer() {
  return { Authorization: `Bearer ${getUserAccessToken()}` };
}

/**
 * Get the area that is linked to a POS configuration.
 *
 * @param {number} groupId group of the POS configuration.
 * @param {number} posConfigId POS configuration id.
 * @returns {Promise<ICheckInConfig>} the area, or null fields if there is none.
 */
export async function getPosCheckInConfig(
  groupId: number,
  posConfigId: number
): Promise<ICheckInConfig> {
  const response = await requestServices({
    method: "GET",
    url: "/access-control/pos/config",
    params: { groupId, posConfigId },
    headers: bearer(),
  });

  return response.data.data;
}

/**
 * Check in tickets of one order. Each ticket gets its own outcome, in the
 * order of the request. A ticket that fails does not stop the other tickets.
 * Only a 409 (order not synced yet) is retried: it comes before any ticket
 * is checked in.
 *
 * @param {string} orderId the order of the tickets.
 * @param {IRedeemTokensPayload} payload the tokens to check in.
 * @returns {Promise<IRedeemResult[]>} one result for each token.
 */
export async function redeemOrderTokens(
  orderId: string,
  payload: IRedeemTokensPayload
): Promise<IRedeemResult[]> {
  const response = await withNotSyncedRetry(() =>
    requestServices({
      method: "POST",
      url: `/access-control/pos/orders/${orderId}/redeem`,
      data: payload,
      headers: bearer(),
    })
  );

  return response.data.data.results || [];
}

/**
 * Check in one ticket from a scanned code.
 *
 * @param {IRedeemByBarCodePayload} payload the scanned code.
 * @returns {Promise<IRedeemByBarCodeResult>} the outcome, with the order and
 * the token of the ticket when the code belongs to one.
 */
export async function redeemByBarCode(
  payload: IRedeemByBarCodePayload
): Promise<IRedeemByBarCodeResult> {
  const response = await withNotSyncedRetry(() =>
    requestServices({
      method: "POST",
      url: "/access-control/pos/orders/redeem-barcode",
      data: payload,
      headers: bearer(),
    })
  );

  return response.data.data;
}

/**
 * Take back the entry of one ticket.
 *
 * @param {string} orderId - The order of the ticket.
 * @param {string} tokenId - The ticket to take back.
 * @param {number} groupId - Group of the POS.
 * @returns {Promise<boolean>} True when the entry was taken back.
 */
export async function unscanOrderToken(
  orderId: string,
  tokenId: string,
  groupId: number
): Promise<boolean> {
  const response = await withNotSyncedRetry(() =>
    requestServices({
      method: "POST",
      url: `/access-control/pos/orders/${orderId}/tokens/${tokenId}/unscan`,
      data: { groupId },
      headers: bearer(),
    })
  );

  return response.data.data?.unscanned === true;
}

/**
 * Get the scans of every ticket of one order.
 *
 * The engine writes its own log on each entry, so this says who is already
 * in. A 409 means the order is not synced yet; the caller decides how long
 * to wait for it.
 *
 * @param {string} orderId - The order to read.
 * @param {number} groupId - Group of the POS.
 * @returns {Promise<IOrderTokenHistory[]>} One entry for each token.
 */
export async function getOrderScanHistory(
  orderId: string,
  groupId: number
): Promise<IOrderTokenHistory[]> {
  const response = await requestServices({
    method: "GET",
    url: `/access-control/pos/orders/${orderId}/scan-history`,
    params: { groupId },
    headers: bearer(),
  });

  return response.data.data || [];
}

/**
 * Get the scans of one ticket, with the time and the operator.
 *
 * @param {string} orderId - The order of the ticket.
 * @param {string} tokenId - The ticket to read.
 * @param {number} groupId - Group of the POS.
 * @returns {Promise<ITicketScanHistory>} The scans of the ticket.
 */
export async function getTokenScanHistory(
  orderId: string,
  tokenId: string,
  groupId: number
): Promise<ITicketScanHistory> {
  const response = await withNotSyncedRetry(() =>
    requestServices({
      method: "GET",
      url: `/access-control/pos/orders/${orderId}/tokens/${tokenId}/scan-history`,
      params: { groupId },
      headers: bearer(),
    })
  );

  return response.data.data;
}
